import type { BaseResponse } from "../../shared/types/admin";

export class ApiError extends Error {
  public readonly status: number;
  public readonly body: unknown;

  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

function toQueryString(query?: Record<string, string | number | boolean | undefined>): string {
  if (!query) return "";
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined) return;
    params.set(key, String(value));
  });
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

function trimTrailingSlash(value: string): string {
  return value.endsWith("/") ? value.slice(0, -1) : value;
}

function configuredApiBaseUrl(): string {
  const explicit = import.meta.env.VITE_ADMIN_API_BASE_URL?.trim();
  if (explicit) {
    return trimTrailingSlash(explicit);
  }

  if (typeof window === "undefined") {
    return "";
  }

  const hostname = window.location.hostname.toLowerCase();
  if (hostname === "admin.wecommend.app") {
    return "https://api.wecommend.app";
  }
  if (hostname === "admin.staging.wecommend.app") {
    return "https://api.staging.wecommend.app";
  }
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "http://localhost:8989";
  }
  return "";
}

export function resolveApiUrl(path: string): string {
  if (/^https?:\/\//.test(path)) {
    return path;
  }

  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const baseUrl = configuredApiBaseUrl();
  if (!baseUrl) {
    return normalizedPath;
  }
  return `${baseUrl}${normalizedPath}`;
}

async function parseResponse<T>(response: Response): Promise<BaseResponse<T>> {
  const text = await response.text();
  if (!text) {
    return { success: response.ok };
  }
  try {
    return JSON.parse(text) as BaseResponse<T>;
  } catch {
    return { success: response.ok, error: text };
  }
}

export async function apiGet<T>(
  path: string,
  token: string,
  query?: Record<string, string | number | boolean | undefined>
): Promise<BaseResponse<T>> {
  const response = await fetch(`${resolveApiUrl(path)}${toQueryString(query)}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"
    }
  });

  const body = await parseResponse<T>(response);
  if (!response.ok) {
    throw new ApiError(`GET ${path} failed`, response.status, body);
  }
  return body;
}

export async function apiPost<T, B>(path: string, body: B, token?: string): Promise<BaseResponse<T>> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json"
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(resolveApiUrl(path), {
    method: "POST",
    headers,
    body: body === undefined ? undefined : JSON.stringify(body)
  });

  const parsed = await parseResponse<T>(response);
  if (!response.ok) {
    throw new ApiError(`POST ${path} failed`, response.status, parsed);
  }
  return parsed;
}

export async function apiPut<T, B>(path: string, body: B, token: string): Promise<BaseResponse<T>> {
  const response = await fetch(resolveApiUrl(path), {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"
    },
    body: JSON.stringify(body)
  });

  const parsed = await parseResponse<T>(response);
  if (!response.ok) {
    throw new ApiError(`PUT ${path} failed`, response.status, parsed);
  }
  return parsed;
}
